import { useCallback } from "react";
import { Trash2, Plus, RotateCcw, Save, Loader2 } from "lucide-react";
import type { ColumnDef, AssumptionSource } from "./types";
import EditableCell from "./EditableCell";

// ---------- types ----------

interface RowData {
  id: string;
  source: AssumptionSource;
  values: Record<string, unknown>;
}

interface ActionButton {
  label: string;
  icon?: React.ReactNode;
  rowTemplate: Record<string, unknown>;
  variant?: "primary" | "secondary";
}

interface TimeSeriesInputTableProps {
  columns: ColumnDef[];
  rows: RowData[];
  onRowChange: (rowId: string, key: string, value: unknown) => void;
  onRowAdd: (template: Record<string, unknown>) => void;
  onRowDelete: (rowId: string) => void;
  onReset: () => void;
  actionButtons: ActionButton[];
  isDirty: boolean;
  onSave: () => void;
  isSaving: boolean;
}

// ---------- main component ----------

export default function TimeSeriesInputTable({
  columns,
  rows,
  onRowChange,
  onRowAdd,
  onRowDelete,
  onReset,
  actionButtons,
  isDirty,
  onSave,
  isSaving,
}: TimeSeriesInputTableProps) {
  const handleNavigate = useCallback(
    (_rowId: string, _colKey: string, _dir: "down" | "right" | "left") => {
      // Navigation is handled by browser focus management;
      // future enhancement: programmatic cell focus via refs
    },
    [],
  );

  return (
    <div className="space-y-3">
      {/* Action buttons */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {actionButtons.map((btn) => (
            <button
              key={btn.label}
              type="button"
              onClick={() => onRowAdd(btn.rowTemplate)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                btn.variant === "primary"
                  ? "bg-forest-700 text-white hover:bg-forest-800"
                  : "bg-white text-bark-600 border border-grove-300 hover:bg-grove-50 hover:shadow-sm"
              }`}
            >
              {btn.icon ?? <Plus className="w-3.5 h-3.5" />}
              {btn.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={!isDirty}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Reset all manual changes"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-grove-100 border-b border-grove-300">
                {columns.map((col) => {
                  const isNum =
                    col.type === "number" ||
                    col.type === "currency" ||
                    col.type === "percent" ||
                    col.type === "year";
                  return (
                    <th
                      key={col.key}
                      className={`px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider whitespace-nowrap ${
                        isNum
                          ? "text-right"
                          : col.type === "checkbox"
                            ? "text-center"
                            : "text-left"
                      }`}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      {col.header}
                    </th>
                  );
                })}
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-8 text-center text-sm text-bark-400"
                  >
                    No entries yet. Use the buttons above to add rows.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-grove-200 last:border-0"
                  >
                    {columns.map((col) => (
                      <EditableCell
                        key={col.key}
                        col={col}
                        value={row.values[col.key]}
                        isManual={row.source === "manual"}
                        onChange={(val) => onRowChange(row.id, col.key, val)}
                        onNavigate={(dir) =>
                          handleNavigate(row.id, col.key, dir)
                        }
                      />
                    ))}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => onRowDelete(row.id)}
                        className="p-1 rounded text-bark-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete row"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || isSaving}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-forest-700 rounded-lg hover:bg-forest-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? "Saving\u2026" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
